import { useMemo } from 'react'
import { useTranslation } from '@disco/i18n/use-translation'
import * as yup from 'yup'

type TranslateFn = ReturnType<typeof useTranslation>['t']

interface SignupInput {
  type: string
  name: string
  label: string
  placeholder?: string
  required?: boolean
  fields?: SignupInput[]
}

const buildEmailSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:emailAddressIsRequired', 'Email address is required'))
    .email(t('accounts:enterAValidEmailAddress', 'Enter a valid email address'))
    .trim()

const buildFullNameSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:fullNameIsRequired', 'Full Name is required'))
    .min(
      2,
      t(
        'accounts:fullNameMustBeAtLeast2Characters',
        'Full name must be at least 2 characters',
      ),
    )
    .trim()

const buildBusinessNameSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:businessNameIsRequired', 'Business Name is required'))
    .min(1, t('accounts:businessNameIsRequired', 'Business Name is required'))
    .trim()

const buildSubdomainSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(t('accounts:discoUrlIsRequired', 'DISCO URL is required'))
    .min(1, t('accounts:discoUrlIsRequired', 'DISCO URL is required'))
    .trim()

const buildUserTypeSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      t('accounts:pleaseSelectAValidOption', 'Please select a valid option'),
    )
    .oneOf(
      ['artist', 'business'],
      t('accounts:pleaseSelectAValidOption', 'Please select a valid option'),
    )

const buildVerificationSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      t(
        'accounts:verificationSourceIsRequired',
        'Verification source is required',
      ),
    )
    .min(
      1,
      t(
        'accounts:verificationSourceIsRequired',
        'Verification source is required',
      ),
    )
    .trim()

const buildTermsAcceptanceSchema = (t: TranslateFn) =>
  yup
    .boolean()
    .oneOf(
      [true],
      t(
        'accounts:youMustAgreeToTheTermsAndPrivacy',
        'You must agree to the Terms of Service and Privacy Policy',
      ),
    )

const buildFileSchema = (t: TranslateFn) =>
  yup
    .mixed()
    .required(t('accounts:fileIsRequired', 'File is required'))
    .test(
      'fileSize',
      t(
        'accounts:fileSizeMustBeLessThan5mb',
        'File size must be less than 5MB',
      ),
      value => {
        if (!value) return true
        const file = value as File
        return file.size <= 5 * 1024 * 1024
      },
    )
    .test(
      'fileType',
      t('accounts:onlyImageFilesAreAllowed', 'Only image files are allowed'),
      value => {
        if (!value) return true
        const file = value as File
        return file.type.startsWith('image/')
      },
    )

// `{{field}} is required` for dynamically-labelled fields.
const requiredField = (t: TranslateFn, field: string) =>
  t('accounts:fieldIsRequired', '{{field}} is required', { field })

const buildUserRoleSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      requiredField(
        t,
        t(
          'accounts:whatBestDescribesYourRole',
          'What best describes your role?',
        ),
      ),
    )

const buildBusinessTypeSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      requiredField(
        t,
        t(
          'accounts:whatBestDescribesYourBusiness',
          'What best describes your business?',
        ),
      ),
    )

const buildTeamSizeSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(
      requiredField(t, t('accounts:sizeOfYourTeam', 'Size of your team')),
    )

const buildCatalogSizeSchema = (t: TranslateFn) =>
  yup
    .string()
    .required(requiredField(t, t('accounts:catalogSize', 'Catalog size')))

const buildStepValidationSchema = (inputs: SignupInput[], t: TranslateFn) => {
  const schemaFields: Record<string, yup.AnySchema> = {}

  inputs.forEach(input => {
    if (input.required) {
      if (input.type === 'verification_pair') {
        input.fields?.forEach((field: SignupInput) => {
          if (field.name.includes('verification_handle')) {
            schemaFields[field.name] = buildVerificationSchema(t)
          } else {
            schemaFields[field.name] = yup
              .string()
              .required(requiredField(t, field.placeholder || field.name))
              .trim()
          }
        })
      } else {
        if (input.type === 'file') {
          schemaFields[input.name] = buildFileSchema(t)
        } else {
          switch (input.name) {
            case 'email': {
              schemaFields[input.name] = buildEmailSchema(t)
              break
            }
            case 'fullName': {
              schemaFields[input.name] = buildFullNameSchema(t)
              break
            }
            case 'business_name': {
              schemaFields[input.name] = buildBusinessNameSchema(t)
              break
            }
            case 'subdomain': {
              schemaFields[input.name] = buildSubdomainSchema(t)
              break
            }
            case 'userType': {
              schemaFields[input.name] = buildUserTypeSchema(t)
              break
            }
            case 'user_role': {
              schemaFields[input.name] = buildUserRoleSchema(t)
              break
            }
            case 'businessType': {
              schemaFields[input.name] = buildBusinessTypeSchema(t)
              break
            }
            case 'teamSize': {
              schemaFields[input.name] = buildTeamSizeSchema(t)
              break
            }
            case 'catalogSize': {
              schemaFields[input.name] = buildCatalogSizeSchema(t)
              break
            }
            case 'termsAcceptance': {
              schemaFields[input.name] = buildTermsAcceptanceSchema(t)
              break
            }
            default:
              schemaFields[input.name] = yup
                .string()
                .required(requiredField(t, input.label))
                .trim()
          }
        }
      }
    }
  })

  return yup.object().shape(schemaFields)
}

export const useValidationSchemas = () => {
  const { t } = useTranslation('accounts')

  return useMemo(
    () => ({
      createStepValidationSchema: (inputs: SignupInput[]) =>
        buildStepValidationSchema(inputs, t),
    }),
    [t],
  )
}
